import React, { useState } from 'react';
import { Box, Grid, Text, Link, List, ListItem, Flex, Input, Button } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';

const MotionBox = motion(Box);

const COL_PLATFORM = [
  { label: 'Modularity',     href: '#modular'       },
  { label: 'Field Service',  href: '#field-service'  },
  { label: 'Applications',   href: '#use-cases'      },
  { label: 'Specifications', href: '#specs'          },
];
const COL_COMPANY = [
  { label: 'About O-ID', href: '#'             },
  { label: 'Research',   href: '#'             },
  { label: 'Careers',    href: '#'             },
  { label: 'Contact',    href: '#contact-form' },
];

function LinkCol({ heading, links }) {
  return (
    <Box>
      <Text fontSize="10px" fontWeight={600} letterSpacing="0.3em"
        textTransform="uppercase" color="rgba(22,33,58,0.4)" mb={5}>
        {heading}
      </Text>
      <List listStyleType="none" m={0} p={0} display="flex" flexDirection="column" gap={1}>
        {links.map(({ label, href }) => (
          <ListItem key={label}>
            <Link href={href} display="inline-flex" alignItems="center"
              minH="44px" fontSize="sm" fontWeight={400}
              color="oid.muted"
              _hover={{ color: 'oid.accent', textDecoration: 'none' }}
              _focusVisible={{ outline: '2px solid', outlineColor: 'oid.accent',
                outlineOffset: '3px', borderRadius: '2px' }}
              transition="color 0.2s ease">
              {label}
            </Link>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

/* ── Social icons ── */
const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452H17.21v-5.569c0-1.327-.024-3.037-1.85-3.037-1.852 0-2.136 1.446-2.136 2.94v5.666H9.989V9h3.111v1.561h.043c.434-.822 1.494-1.688 3.073-1.688 3.286 0 3.893 2.163 3.893 4.976v6.603zM5.337 7.433a1.805 1.805 0 1 1 0-3.61 1.805 1.805 0 0 1 0 3.61zM6.961 20.452H3.71V9h3.251v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.258 5.63 5.906-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

function Footer() {
  const shouldReduce = useReducedMotion();
  const [email, setEmail]     = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribed(true);
  };

  return (
    <Box as="footer" id="contact" aria-label="Site footer" bg="white">
      <MotionBox
        initial={shouldReduce ? false : { opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
        py={{ base: '55px', md: '89px' }}>
        <Grid templateColumns={{ base: '1fr', md: '38.2% 1fr 1fr' }}
          gap={{ base: 12, md: 16 }} alignItems="start"
          maxWidth="1600px" mx="auto" px={{ base: 6, lg: 12 }}>

          {/* Brand column */}
          <Box>
            <Link href="#" display="inline-block" mb={5}
              _hover={{ textDecoration: 'none' }}
              _focusVisible={{ outline: '2px solid', outlineColor: 'oid.accent',
                outlineOffset: '3px', borderRadius: '2px' }}
              aria-label="O-ID — return to top">
              <Box as="img" src="/o-id-logo-with-text.png" alt="O-ID"
                sx={{ width: '120px', height: 'auto' }} />
            </Link>
            <Text fontSize="sm" fontWeight={400} color="oid.muted"
              lineHeight={1.618} maxWidth="240px" mb={6}>
              Modular Humanoid Robotics.
              <br />Designed for the real world.
            </Text>

            {/* Newsletter */}
            <Text fontSize="10px" fontWeight={600} letterSpacing="0.26em"
              textTransform="uppercase" color="rgba(22,33,58,0.4)" mb={3}>
              Newsletter
            </Text>
            {subscribed ? (
              <Flex align="center" gap={2} height="44px">
                <Box width="5px" height="5px" borderRadius="full" bg="oid.accent" flexShrink={0} />
                <Text fontSize="xs" color="oid.muted">
                  You're subscribed.
                </Text>
              </Flex>
            ) : (
              <Flex as="form" onSubmit={handleSubscribe} gap={2} mb={6}>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  flex={1}
                  bg="oid.bg"
                  border="1px solid"
                  borderColor="rgba(22,33,58,0.18)"
                  borderRadius="2px"
                  color="oid.text"
                  fontSize="sm"
                  height="44px"
                  _placeholder={{ color: 'rgba(22,33,58,0.35)' }}
                  _focus={{ borderColor: 'oid.accent', boxShadow: 'none' }}
                  _hover={{ borderColor: 'rgba(22,33,58,0.3)' }}
                />
                <Button type="submit" height="44px" px={5}
                  fontSize="10px" letterSpacing="0.1em" textTransform="uppercase" flexShrink={0}>
                  Subscribe
                </Button>
              </Flex>
            )}

            {/* Social links */}
            <Flex gap={3} mt={subscribed ? 4 : 0}>
              <Link href="https://linkedin.com" isExternal
                display="flex" alignItems="center" justifyContent="center"
                width="36px" height="36px"
                color="rgba(22,33,58,0.4)"
                border="1px solid rgba(22,33,58,0.18)"
                borderRadius="2px"
                _hover={{ color: 'oid.accent', borderColor: 'oid.accent', textDecoration: 'none' }}
                _focusVisible={{ outline: '2px solid', outlineColor: 'oid.accent', outlineOffset: '2px' }}
                transition="color 0.2s ease, border-color 0.2s ease"
                aria-label="O-ID on LinkedIn">
                <LinkedInIcon />
              </Link>
              <Link href="https://x.com" isExternal
                display="flex" alignItems="center" justifyContent="center"
                width="36px" height="36px"
                color="rgba(22,33,58,0.4)"
                border="1px solid rgba(22,33,58,0.18)"
                borderRadius="2px"
                _hover={{ color: 'oid.accent', borderColor: 'oid.accent', textDecoration: 'none' }}
                _focusVisible={{ outline: '2px solid', outlineColor: 'oid.accent', outlineOffset: '2px' }}
                transition="color 0.2s ease, border-color 0.2s ease"
                aria-label="O-ID on X (Twitter)">
                <XIcon />
              </Link>
            </Flex>
          </Box>

          <LinkCol heading="Platform" links={COL_PLATFORM} />
          <LinkCol heading="Company"  links={COL_COMPANY}  />
        </Grid>
      </MotionBox>

      {/* Bottom bar */}
      <Box borderTop="1px solid rgba(22,33,58,0.1)" py={5}>
        <Flex justify="space-between" align="center"
          flexDirection={{ base: 'column', sm: 'row' }} gap={3}
          maxWidth="1600px" mx="auto" px={{ base: 6, lg: 12 }}>
          <Text fontSize="xs" fontWeight={400} color="rgba(22,33,58,0.4)"
            letterSpacing="0.05em">
            © 2026 O-ID Robotics. All rights reserved.
          </Text>
          <Text fontSize="10px" fontWeight={600} letterSpacing="0.28em"
            textTransform="uppercase" color="rgba(22,33,58,0.25)">
            O·ID Series I Field Edition
          </Text>
        </Flex>
      </Box>
    </Box>
  );
}
export default Footer;
